import { useState, useCallback } from 'react';
import {
  savePlayerDataFile,
  listPlayerDataFiles,
  loadPlayerDataFile,
  deletePlayerDataFile,
  renamePlayerDataFile
} from '../services/playerDataService';

const emptyPositions = () => ({
  QB: [],
  RB: [],
  WR: [],
  TE: [],
  K: [],
  DEF: []
});

// Projection files may contain records with `projected: null` (players the
// model couldn't project). They can't be valued or drafted, and downstream
// code assumes `projected` is a number, so drop them wherever player data
// enters the app.
export const sanitizePlayerData = (data) => {
  const cleaned = emptyPositions();
  Object.keys(cleaned).forEach(position => {
    cleaned[position] = (data[position] || []).filter(
      player => typeof player.projected === 'number'
    );
  });
  return cleaned;
};

export const usePlayerData = (username) => {
  const [customPlayerData, setCustomPlayerData] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState(emptyPositions);
  const [savedFiles, setSavedFiles] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [loadingFileName, setLoadingFileName] = useState(null);

  const validatePlayerData = (data) => {
    const requiredPositions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
    const requiredFields = ['name', 'team', 'projected'];

    return requiredPositions.every(pos =>
      Array.isArray(data[pos]) &&
      data[pos].every(player =>
        requiredFields.every(field => player.hasOwnProperty(field))
      )
    );
  };

  const applyPlayerData = (data) => {
    const cleaned = sanitizePlayerData(data);
    setCustomPlayerData(cleaned);
    setAvailablePlayers({
      QB: [...cleaned.QB],
      RB: [...cleaned.RB],
      WR: [...cleaned.WR],
      TE: [...cleaned.TE],
      K: [...cleaned.K],
      DEF: [...cleaned.DEF]
    });
  };

  const refreshSavedFiles = useCallback(async () => {
    if (!username) return;
    try {
      setSavedFiles(await listPlayerDataFiles(username));
    } catch (error) {
      console.error('Error listing player data files:', error);
    }
  }, [username]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const text = await file.text();
        const data = JSON.parse(text);

        if (validatePlayerData(data)) {
          applyPlayerData(data);

          // Store the file in the user's account so it can be reused later.
          // An existing file with the same name is overwritten (the natural
          // way to update projections).
          const fileName = file.name.replace(/\.json$/i, '');
          try {
            await savePlayerDataFile(username, fileName, data);
            setSelectedFileName(fileName);
            await refreshSavedFiles();
          } catch (error) {
            console.error('Error saving player data file to account:', error);
            alert('Player data loaded for this session, but saving it to your account failed.');
          }
        } else {
          alert('Invalid file format. Please ensure the JSON matches the required structure.');
        }
      } catch (error) {
        console.error('Error reading file:', error);
        alert('Error reading file. Please ensure it is a valid JSON file.');
      }
    }
  };

  const selectSavedFile = async (fileName) => {
    try {
      setLoadingFileName(fileName);
      const data = await loadPlayerDataFile(username, fileName);
      if (!validatePlayerData(data)) {
        alert('This file no longer matches the required structure. Please upload it again.');
        return;
      }
      applyPlayerData(data);
      setSelectedFileName(fileName);
    } catch (error) {
      console.error('Error loading player data file:', error);
      alert('Failed to load player data file. Please try again.');
    } finally {
      setLoadingFileName(null);
    }
  };

  const deleteSavedFile = async (fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }
    try {
      await deletePlayerDataFile(username, fileName);
      setSavedFiles(files => files.filter(f => f.name !== fileName));
      if (selectedFileName === fileName) {
        setSelectedFileName(null);
      }
    } catch (error) {
      console.error('Error deleting player data file:', error);
      alert('Failed to delete player data file. Please try again.');
    }
  };

  const renameSavedFile = async (fileName) => {
    const newName = window.prompt('New file name:', fileName)?.trim();
    if (!newName || newName === fileName) return;
    if (savedFiles.some(f => f.name === newName)) {
      alert('A file with this name already exists.');
      return;
    }
    try {
      await renamePlayerDataFile(username, fileName, newName);
      setSavedFiles(files => files.map(f => (f.name === fileName ? { ...f, name: newName } : f)));
      if (selectedFileName === fileName) {
        setSelectedFileName(newName);
      }
    } catch (error) {
      console.error('Error renaming player data file:', error);
      alert(`Failed to rename player data file: ${error.message}`);
    }
  };

  const resetPlayerData = () => {
    setCustomPlayerData(null);
    setAvailablePlayers(emptyPositions());
    setSelectedFileName(null);
  };

  return {
    customPlayerData,
    setCustomPlayerData,
    availablePlayers,
    setAvailablePlayers,
    savedFiles,
    selectedFileName,
    loadingFileName,
    refreshSavedFiles,
    handleFileUpload,
    selectSavedFile,
    deleteSavedFile,
    renameSavedFile,
    resetPlayerData
  };
};
